import { useRef, useEffect, useState, useCallback } from "react";
import { motion, useInView } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTheme } from "@/components/ThemeProvider";
import framerCommerceIcon from "@/assets/tools/framer-commerce.png";
import contraIcon from "@/assets/tools/contra.svg";
import lovableIcon from "@/assets/tools/lovable.svg";
import canvaIcon from "@/assets/tools/canva.webp";
import slackIcon from "@/assets/tools/slack.png";
import readymagIcon from "@/assets/tools/readymag.png";

interface Tool {
  name: string;
  icon: string;
  /** Optional dark mode icon URL (used instead of CSS invert) */
  iconDark?: string;
  /** Set true for local monochrome assets that should CSS-invert in dark mode */
  invertInDark?: boolean;
}

const tools: Tool[] = [
  { name: "Framer", icon: "https://cdn.simpleicons.org/framer/000000", iconDark: "https://cdn.simpleicons.org/framer/ffffff" },
  { name: "Framer Commerce", icon: framerCommerceIcon, invertInDark: true },
  { name: "Figma", icon: "https://cdn.simpleicons.org/figma" },
  { name: "Contra", icon: contraIcon, invertInDark: true },
  { name: "Shopify", icon: "https://cdn.simpleicons.org/shopify" },
  { name: "Asana", icon: "https://cdn.simpleicons.org/asana" },
  { name: "ClickUp", icon: "https://cdn.simpleicons.org/clickup" },
  { name: "Zapier", icon: "https://cdn.simpleicons.org/zapier" },
  { name: "Canva", icon: canvaIcon },
  { name: "Lovable", icon: lovableIcon },
  { name: "Notion", icon: "https://cdn.simpleicons.org/notion/000000", iconDark: "https://cdn.simpleicons.org/notion/ffffff" },
  { name: "Slack", icon: slackIcon },
  { name: "Loom", icon: "https://cdn.simpleicons.org/loom/000000", iconDark: "https://cdn.simpleicons.org/loom/ffffff" },
  { name: "Google", icon: "https://cdn.simpleicons.org/google" },
  { name: "Dribbble", icon: "https://cdn.simpleicons.org/dribbble" },
  { name: "Awwwards", icon: "https://cdn.simpleicons.org/awwwards/000000", iconDark: "https://cdn.simpleicons.org/awwwards/ffffff" },
  { name: "Behance", icon: "https://cdn.simpleicons.org/behance/000000", iconDark: "https://cdn.simpleicons.org/behance/ffffff" },
  { name: "GoDaddy", icon: "https://cdn.simpleicons.org/godaddy" },
  { name: "Jira", icon: "https://cdn.simpleicons.org/jira" },
  { name: "Readymag", icon: readymagIcon },
  { name: "Squarespace", icon: "https://cdn.simpleicons.org/squarespace/000000", iconDark: "https://cdn.simpleicons.org/squarespace/ffffff" },
];

const REPEL_RADIUS = 90;
const REPEL_STRENGTH = 14;
const SPRING_DAMPING = 0.82;
const SPRING_STIFFNESS = 0.1;

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  homeX: number;
  homeY: number;
}

/* ── Dual-row marquee for mobile ── */
const TickerCarousel = () => {
  const row1 = tools.slice(0, Math.ceil(tools.length / 2));
  const row2 = tools.slice(Math.ceil(tools.length / 2));
  const doubled1 = [...row1, ...row1, ...row1];
  const doubled2 = [...row2, ...row2, ...row2];

  return (
    <div className="flex flex-col gap-3 -mx-5 md:-mx-10 lg:-mx-14 overflow-hidden">
      {/* Row 1 — scrolls left */}
      <div className="relative">
        <div className="flex animate-[ticker_35s_linear_infinite] gap-3 w-max">
          {doubled1.map((tool, i) => (
            <div
              key={`r1-${tool.name}-${i}`}
              className="flex-shrink-0 flex items-center gap-2.5 px-4 py-2.5 border border-foreground/[0.08] bg-foreground/[0.03] rounded-full"
            >
              <ToolIcon tool={tool} size="w-4 h-4" />
               <span className="font-body text-ts-2xs tracking-[0.05em] text-foreground/60 whitespace-nowrap">
                 {tool.name}
               </span>
            </div>
          ))}
        </div>
      </div>
      {/* Row 2 — scrolls right */}
      <div className="relative">
        <div className="flex animate-[ticker-reverse_40s_linear_infinite] gap-3 w-max">
          {doubled2.map((tool, i) => (
            <div
              key={`r2-${tool.name}-${i}`}
              className="flex-shrink-0 flex items-center gap-2.5 px-4 py-2.5 border border-foreground/[0.08] bg-foreground/[0.03] rounded-full"
            >
              <ToolIcon tool={tool} size="w-4 h-4" />
             <span className="font-body text-ts-2xs tracking-[0.05em] text-foreground/60 whitespace-nowrap">
               {tool.name}
             </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Icon with fallback to initials ── */
const ToolIcon = ({ tool, size = "w-5 h-5 md:w-6 md:h-6" }: { tool: Tool; size?: string }) => {
   const [failed, setFailed] = useState(false);
   const { mode } = useTheme();
   const isDark = mode === "dark";
   const iconSrc = isDark && tool.iconDark ? tool.iconDark : tool.icon;

   if (failed) {
     return (
       <span className={`${size} flex items-center justify-center font-body text-ts-2xs font-bold text-foreground/60 select-none`}>
        {tool.name.slice(0, 2).toUpperCase()}
      </span>
    );
  }

  return (
    <img
      src={iconSrc}
      alt={tool.name}
      className={`${size} object-contain ${tool.invertInDark ? "dark:invert" : ""}`}
      loading="lazy"
      decoding="async"
      width={24}
      height={24}
      draggable={false}
      onError={() => setFailed(true)}
    />
  );
};

/* ── Desktop foam pit ── */
const FoamPit = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: true, margin: "-40px" });
  const particlesRef = useRef<Particle[]>([]);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  const rafRef = useRef<number>(0);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [ready, setReady] = useState(false);

  const initParticles = useCallback(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const items = container.querySelectorAll<HTMLDivElement>("[data-tool]");
    const particles: Particle[] = [];
    items.forEach((item) => {
      const r = item.getBoundingClientRect();
      const x = r.left - rect.left + r.width / 2;
      const y = r.top - rect.top + r.height / 2;
      particles.push({ x, y, vx: 0, vy: 0, homeX: x, homeY: y });
    });
    particlesRef.current = particles;
    setReady(true);
  }, []);

  useEffect(() => {
    if (!isInView) return;
    const t = setTimeout(initParticles, 600);
    return () => clearTimeout(t);
  }, [isInView, initParticles]);

  useEffect(() => {
    if (!ready || !containerRef.current) return;
    const container = containerRef.current;

    const onMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const onLeave = () => { mouseRef.current = { x: -9999, y: -9999 }; };

    container.addEventListener("mousemove", onMove);
    container.addEventListener("mouseleave", onLeave);

    const tick = () => {
      const { x: mx, y: my } = mouseRef.current;
      particlesRef.current.forEach((p, i) => {
        const dx = p.x - mx;
        const dy = p.y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < REPEL_RADIUS && dist > 0) {
          const force = (1 - dist / REPEL_RADIUS) * REPEL_STRENGTH;
          const angle = Math.atan2(dy, dx);
          p.vx += Math.cos(angle) * force * 0.08;
          p.vy += Math.sin(angle) * force * 0.08;
        }
        p.vx += (p.homeX - p.x) * SPRING_STIFFNESS;
        p.vy += (p.homeY - p.y) * SPRING_STIFFNESS;
        p.vx *= SPRING_DAMPING;
        p.vy *= SPRING_DAMPING;
        p.x += p.vx;
        p.y += p.vy;
        const el = itemRefs.current[i];
        if (el) {
          const ox = p.x - p.homeX;
          const oy = p.y - p.homeY;
          el.style.transform = `translate(${ox}px, ${oy}px) rotate(${ox * 0.15}deg)`;
        }
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      container.removeEventListener("mousemove", onMove);
      container.removeEventListener("mouseleave", onLeave);
    };
  }, [ready]);

  return (
    <div ref={containerRef} className="relative flex flex-wrap gap-3 md:gap-4 py-4">
      {tools.map((tool, i) => (
        <motion.div
          key={tool.name}
          data-tool={tool.name}
          ref={(el) => { itemRefs.current[i] = el; }}
          initial={{ opacity: 0, y: 16, scale: 0.9 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ delay: i * 0.03, duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="will-change-transform"
          title={tool.name}
        >
          <div className="w-11 h-11 md:w-13 md:h-13 rounded-xl border border-foreground/10 bg-foreground/[0.04] flex items-center justify-center cursor-default select-none">
            <ToolIcon tool={tool} />
          </div>
        </motion.div>
      ))}
    </div>
  );
};

/* ── Main ── */
const ToolStack = () => {
  const isMobile = useIsMobile();

  return (
    <div className="mt-16 md:mt-24">
       <p className="font-body text-ts-2xs font-semibold tracking-[0.25em] uppercase text-muted-foreground mb-6">
         Tool Stack
       </p>
      {isMobile ? <TickerCarousel /> : <FoamPit />}
    </div>
  );
};

export default ToolStack;
